import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTenant } from "@/lib/tenant";
import { ArrowLeft, Loader2, Tractor, Sprout } from "lucide-react";

export default function FleetOperations() {
  const { api } = useTenant();
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/fieldops/operations?limit=200")
      .then(r => setOps(r.data?.data || []))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const filtered = ops.filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [o.task, o.crop, o.field, o.vin, o.description].some(
      v => v && String(v).toLowerCase().includes(s));
  });

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <Link to="/admin/fleet" className="text-sm text-slate-400 hover:text-amber-300 inline-flex items-center gap-1 mb-1">
            <ArrowLeft size={14} /> Back to fleet
          </Link>
          <h1 className="text-2xl font-bold">Operations history</h1>
          <p className="text-sm text-slate-400">Every job done by your tractors on FieldOps — task, crop, field, time.</p>
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search task / crop / field / VIN…"
               data-testid="ops-search"
               className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm w-72" />
      </div>

      {loading ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
      ) : !filtered.length ? (
        <div className="bg-[#0a0e1c] border border-white/10 rounded-lg p-10 text-center text-slate-500">
          {ops.length === 0
            ? "No operations recorded yet (or FieldOps hasn't pushed any to this account)."
            : "No operations match your search."}
        </div>
      ) : (
        <div className="bg-[#0a0e1c] border border-white/10 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-slate-400">
              <tr>
                <th className="text-left px-3 py-3">Start</th>
                <th className="text-left px-3 py-3">Task</th>
                <th className="text-left px-3 py-3">Crop</th>
                <th className="text-left px-3 py-3">Field</th>
                <th className="text-left px-3 py-3">Machine</th>
                <th className="text-left px-3 py-3">Season</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o, i) => (
                <tr key={o.id || i} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-300">
                    {o.start ? new Date(o.start).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </td>
                  <td className="px-3 py-2.5 font-medium">{o.task || o.description || "—"}</td>
                  <td className="px-3 py-2.5"><span className="inline-flex items-center gap-1 text-emerald-300"><Sprout size={11} /> {o.crop || "—"}</span></td>
                  <td className="px-3 py-2.5 text-slate-300">{o.field || "—"}</td>
                  <td className="px-3 py-2.5">
                    {o.vin ? (
                      <Link to={`/admin/fleet/${o.vin}`} className="text-amber-300 hover:underline inline-flex items-center gap-1">
                        <Tractor size={11} /> {o.vin.slice(-6)}
                      </Link>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-slate-400">{o.season || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
